import api from '@flatfile/api';
import { firstValueFrom, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { FlatfileListener } from '@flatfile/listener';
import { bulkRecordHook, recordHook } from '@flatfile/plugin-record-hook';
import { InspectionService } from '@proxy/servicing-service/inspections/inspection.service';
import { InspectionTypeService, RecurrenceType } from '@proxy/servicing-service/inspection-types';
import { AssetService } from '@/proxy/register-service/assets/asset.service';
import DateInputHelper, { DateInputResolution } from '@/shared/utils/date-input.helper';

type DateField = 'doneOn' | 'dueOn';

@Injectable({
  providedIn: 'root',
})
export class CustomListenerService {
  jobCompleted$: Subject<void> = new Subject<void>();
  closeSpace$: Subject<void> = new Subject<void>();
  errorDetails: { id: string; locations: { id: string; fullName: string }[] }[] = [];
  private readonly typeRecurrenceById = new Map<string, RecurrenceType | undefined>();

  constructor(
    private service: InspectionService,
    private assetService: AssetService,
    private inspectionTypeService: InspectionTypeService,
  ) {}

  public setInspectionTypeRecurrences(
    types: ({ id?: string; recurrence?: RecurrenceType } | null | undefined)[],
  ) {
    types.forEach(type => {
      if (type?.id) {
        this.typeRecurrenceById.set(type.id, type.recurrence);
      }
    });
  }

  private getRecordValue(record: any, field: string) {
    return record?.values?.[field]?.value ?? record?.get?.(field);
  }

  private getTypeId(record: any) {
    return this.getRecordValue(record, 'typeId');
  }

  private isPeriodicType(typeId: string | null | undefined) {
    return !!typeId && this.typeRecurrenceById.get(typeId) === RecurrenceType.Periodic;
  }

  private async ensureInspectionTypeRecurrences(records: any[]) {
    const uncachedTypeIds = [
      ...new Set(
        records
          .map(record => this.getTypeId(record))
          .filter((typeId): typeId is string => !!typeId),
      ),
    ].filter(typeId => !this.typeRecurrenceById.has(typeId));

    if (!uncachedTypeIds.length) {
      return;
    }

    const types = await Promise.all(
      uncachedTypeIds.map(typeId =>
        firstValueFrom(this.inspectionTypeService.get(typeId)).catch(() => null),
      ),
    );

    this.setInspectionTypeRecurrences(types);
  }

  private normalizeRecordDateField(record: any, field: DateField) {
    const resolution = DateInputHelper.resolveDateValue(record.get(field));

    if (resolution.parsedDate && resolution.value !== resolution.rawValue) {
      record.set(field, resolution.value);
    }

    return resolution;
  }

  private addValidationMessages(record: any, field: DateField, messages: string[]) {
    messages.forEach(message => record.addError(field, message));
  }

  private validateDoneOn(record: any, doneOn: DateInputResolution, dueOn: DateInputResolution) {
    const messages: string[] = [];
    if (!doneOn.rawValue) {
      return;
    }

    if (!doneOn.parsedDate) {
      messages.push('Use DD/MM/YY or DD/MM/YYYY.');
      this.addValidationMessages(record, 'doneOn', messages);
      return;
    }

    if (DateInputHelper.isFutureDate(doneOn.parsedDate)) {
      messages.push('The "doneOn" date cannot be in the future.');
    }

    if (dueOn.parsedDate) {
      if (doneOn.parsedDate.getTime() === dueOn.parsedDate.getTime()) {
        messages.push('The "doneOn" date cannot be the same as "dueOn".');
      } else if (doneOn.parsedDate.getTime() > dueOn.parsedDate.getTime()) {
        messages.push('The "doneOn" date must be earlier than "dueOn".');
      }
    }

    this.addValidationMessages(record, 'doneOn', messages);
  }

  private validateDueOn(record: any, dueOn: DateInputResolution, doneOn: DateInputResolution) {
    const messages: string[] = [];
    if (!dueOn.rawValue) {
      return;
    }

    if (!dueOn.parsedDate) {
      messages.push('The "dueOn" date is invalid. Use DD/MM/YY or DD/MM/YYYY.');
      this.addValidationMessages(record, 'dueOn', messages);
      return;
    }

    if (!DateInputHelper.isFutureDate(dueOn.parsedDate)) {
      messages.push('The "dueOn" date must be later than today.');
    }

    if (doneOn.parsedDate) {
      if (dueOn.parsedDate.getTime() === doneOn.parsedDate.getTime()) {
        messages.push('The "dueOn" date cannot be the same as "doneOn".');
      } else if (dueOn.parsedDate.getTime() < doneOn.parsedDate.getTime()) {
        messages.push('The "dueOn" date must be later than "doneOn".');
      }
    }

    this.addValidationMessages(record, 'dueOn', messages);
  }

  private validateRequiredDueOn(record: any, dueOn: DateInputResolution) {
    if (!this.isPeriodicType(this.getTypeId(record)) || dueOn.rawValue) {
      return;
    }

    record.addError(
      'dueOn',
      'The "dueOn" date is required when the selected event type recurrence is periodic.',
    );
  }

  private getSubmissionValue(record: any, field: DateField) {
    const resolution = DateInputHelper.resolveDateValue(record?.values?.[field]?.value);

    return resolution.value ?? resolution.rawValue;
  }

  private normalizeRecordsForSubmission(records: any[]) {
    return records.map(record => ({
      ...record,
      values: {
        ...record.values,
        doneOn: {
          ...record.values?.doneOn,
          value: this.getSubmissionValue(record, 'doneOn'),
        },
        dueOn: {
          ...record.values?.dueOn,
          value: this.getSubmissionValue(record, 'dueOn'),
        },
      },
    }));
  }

  private hasMissingRequiredDueOn(records: any[]) {
    return records.some(record => {
      const dueOn = DateInputHelper.resolveDateValue(this.getRecordValue(record, 'dueOn'));

      return this.isPeriodicType(this.getTypeId(record)) && !dueOn.rawValue;
    });
  }

  public listener = FlatfileListener.create(listener => {
    listener.use(
      recordHook('events', record => {
        const matchingError = this.errorDetails.find(e => e.id === record.rowId);
        if (matchingError) {
          record.addError('targetLocationId', 'Selected location does not exist in this account.');
        }

        const doneOn = this.normalizeRecordDateField(record, 'doneOn');
        const dueOn = this.normalizeRecordDateField(record, 'dueOn');

        this.validateRequiredDueOn(record, dueOn);
        this.validateDoneOn(record, doneOn, dueOn);
        this.validateDueOn(record, dueOn, doneOn);

        return record;
      }),
    );

    listener.on('job:outcome-acknowledged', event => {
      this.closeSpace$.next();
    });

    listener.use(
      bulkRecordHook('events', records => {
        records.map(record => {
          record.compute('pdfDocument', (pdfDocument, record) => {
            const assetId = record.get('assetId') || '';
            const dueOn = DateInputHelper.resolveDateValue(record.get('dueOn'));
            let dueOnFormatted = '';

            if (dueOn.parsedDate) {
              dueOnFormatted = DateInputHelper.formatDate(dueOn.parsedDate);
            }

            return `${assetId}-${dueOnFormatted}.pdf`;
          });
          return record;
        });
      }),
    );

    listener.filter({ job: 'workbook:submitActionFg' }, configure => {
      configure.on('job:ready', async ({ context: { jobId, workbookId } }) => {
        try {
          await api.jobs.ack(jobId, {
            info: 'Getting started.',
            progress: 10,
          });
          const sheets = await api.sheets.list({ workbookId }).catch(err => {
            throw new Error(`Failed to fetch sheets: ${err.message}`);
          });
          const allData = await Promise.all(
            sheets.data.map(async sheet => {
              try {
                const records = await api.records.get(sheet.id);
                return records.data;
              } catch (err: any) {
                throw new Error(`Failed to fetch records for sheet ${sheet.id}: ${err.message}`);
              }
            }),
          );
          const records = allData[0]?.records || [];
          if (!records.length) {
            throw new Error('No records found in the workbook.');
          }

          await this.ensureInspectionTypeRecurrences(records);

          if (this.hasMissingRequiredDueOn(records)) {
            await api.jobs.fail(jobId, {
              outcome: {
                message:
                  'Bulk upload failed. Due On is required when the selected event type recurrence is periodic.',
              },
            });
            return;
          }

          this.assetService.validateLocations(records).subscribe({
            next: async data => {
              this.errorDetails = data;

              if (data.length) {
                await api.jobs.fail(jobId, {
                  outcome: {
                    message: 'Bulk upload failed. Please check the data and try again.',
                  },
                });
                api.sheets.validate(sheets.data[0].id);
              } else {
                await api.jobs.ack(jobId, {
                  info: 'Validation successful. Starting bulk upload.',
                  progress: 50,
                });

                this.service.bulkUpload(this.normalizeRecordsForSubmission(records)).subscribe({
                  next: async () => {
                    await api.jobs.complete(jobId, {
                      outcome: {
                        message: 'Action completed successfully.',
                        acknowledge: true,
                      },
                    });
                    this.jobCompleted$.next();
                  },
                  error: async () => {
                    await api.jobs.fail(jobId, {
                      outcome: {
                        message: 'Bulk upload failed. Please check the data and try again.',
                      },
                    });
                  },
                });
              }
            },
          });
        } catch (error: any) {
          console.error('Error:', error.stack);
          await api.jobs.fail(jobId, {
            outcome: {
              message: 'This job encountered an error.',
            },
          });
        }
      });
    });
  });
}
